import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, XCircle, RefreshCw, Eye, Download, Calendar, Filter } from 'lucide-react';
import { Card } from '../BossUI';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';

const ExtractionStatusBadge = ({ status }) => {
  const configs = {
    success: { 
      icon: CheckCircle, 
      className: 'bg-green-100 text-green-800 border-green-200',
      text: 'Success'
    },
    failed: { 
      icon: XCircle, 
      className: 'bg-red-100 text-red-800 border-red-200',
      text: 'Failed'
    },
    partial: { 
      icon: AlertCircle, 
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      text: 'Partial'
    }
  };

  const config = configs[status] || configs.failed;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.text}
    </span>
  );
};

const ExtractionLogModal = ({ log, onClose }) => {
  if (!log) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            Extraction Log Details
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-500"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">File Name</label>
              <p className="text-sm text-slate-900">{log.fileName}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">Service Type</label>
              <p className="text-sm text-slate-900">{log.serviceType}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">Status</label>
              <div className="mt-1">
                <ExtractionStatusBadge status={log.extractionStatus} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">Fields Extracted</label>
              <p className="text-sm text-slate-900">{log.fieldsExtracted || 0}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">Attempted At</label>
              <p className="text-sm text-slate-900">
                {log.attemptedAt ? new Date(log.attemptedAt).toLocaleString() : 'Unknown'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">Processed By</label>
              <p className="text-sm text-slate-900">{log.processedBy || 'System'}</p>
            </div>
          </div>

          {/* Error Message */}
          {log.errorMessage && (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">Error Message</label>
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800 whitespace-pre-wrap">{log.errorMessage}</p>
              </div>
            </div>
          )}

          {/* Extracted Data */}
          {log.extractedData && (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase">Extracted Data</label>
              <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-md">
                <pre className="text-xs text-slate-700 whitespace-pre-wrap overflow-x-auto">
                  {typeof log.extractedData === 'string' 
                    ? log.extractedData 
                    : JSON.stringify(log.extractedData, null, 2)
                  }
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-800 focus:outline-none"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const DocumentExtractionManagement = () => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const { token, hasRole } = useAuth();
  const queryClient = useQueryClient();

  // Query for extraction logs
  const { data: logsData, isLoading, error } = useQuery({
    queryKey: ['extraction-logs', statusFilter, serviceTypeFilter, dateFilter],
    queryFn: async () => {
      let url = `${process.env.REACT_APP_API_URL}/admin/extraction-logs?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (serviceTypeFilter !== 'all') url += `serviceType=${serviceTypeFilter}&`;
      if (dateFilter) url += `date=${dateFilter}&`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch extraction logs');
      return response.json();
    },
    enabled: hasRole('admin')
  });

  // Mutation for retrying extraction
  const retryExtraction = useMutation({
    mutationFn: async (logId) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/extraction-logs/${logId}/retry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || 'Failed to retry extraction');
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['extraction-logs']);
      alert(`Extraction retry ${data.success ? 'successful' : 'failed'}: ${data.message}`);
    },
    onError: (error) => {
      alert(`Retry failed: ${error.message}`);
    }
  });

  // Mutation for downloading document
  const downloadDocument = useMutation({
    mutationFn: async ({ reportId, fileName }) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/session-reports/${reportId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch document');
      
      const documents = await response.json();
      const document = documents.find(doc => doc.fileName === fileName);
      if (!document) throw new Error('Document not found');
      
      // Download the specific document
      const downloadResponse = await fetch(`${process.env.REACT_APP_API_URL}/session-reports/documents/${document.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!downloadResponse.ok) throw new Error('Failed to download document');
      
      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onError: (error) => {
      alert(`Download failed: ${error.message}`);
    }
  });

  if (!hasRole('admin')) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You do not have permission to access document extraction management.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load extraction logs: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  const logs = logsData?.logs || [];
  const stats = logsData?.stats || { total: 0, successful: 0, failed: 0, partial: 0 };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Document Extraction Management</h2>
        <p className="mt-1 text-sm text-slate-500">
          Monitor and manage automatic document extraction from assessment forms
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-blue-100 rounded-md flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-slate-500">Total Attempts</p>
              <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 rounded-md flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-slate-500">Successful</p>
              <p className="text-2xl font-semibold text-slate-900">{stats.successful}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-red-100 rounded-md flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-slate-500">Failed</p>
              <p className="text-2xl font-semibold text-slate-900">{stats.failed}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-yellow-100 rounded-md flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-slate-500">Partial</p>
              <p className="text-2xl font-semibold text-slate-900">{stats.partial}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-slate-400" />
          <div className="flex-1 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="success">Successful</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
            </select>

            <select
              value={serviceTypeFilter}
              onChange={(e) => setServiceTypeFilter(e.target.value)}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="all">All Service Types</option>
              <option value="Initial Assessment">Initial Assessment</option>
              <option value="12 Week Review">12 Week Review</option>
            </select>

            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Filter by date"
            />
          </div>
        </div>
      </Card>

      {/* Extraction Logs Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Service Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Fields
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Attempted
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {logs.map((log) => (
                <tr key={log.logId} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {log.fileName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {log.serviceType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ExtractionStatusBadge status={log.extractionStatus} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {log.fieldsExtracted || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {log.attemptedAt ? new Date(log.attemptedAt).toLocaleDateString() : 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    
                    {log.reportId && (
                      <button
                        onClick={() => downloadDocument.mutate({ 
                          reportId: log.reportId, 
                          fileName: log.fileName 
                        })}
                        disabled={downloadDocument.isLoading}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        title="Download Document"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                    
                    {log.extractionStatus === 'failed' && (
                      <button
                        onClick={() => retryExtraction.mutate(log.logId)}
                        disabled={retryExtraction.isLoading}
                        className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                        title="Retry Extraction"
                      >
                        <RefreshCw className={`h-4 w-4 ${retryExtraction.isLoading ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No extraction logs found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {statusFilter !== 'all' || serviceTypeFilter !== 'all' || dateFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Extraction logs will appear here as documents are processed.'}
            </p>
          </div>
        )}
      </Card>

      {/* Log Details Modal */}
      {selectedLog && (
        <ExtractionLogModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
};

export default DocumentExtractionManagement;